import { toastService as toast, toastService } from "../../../components/ui/toast/toast.service";
import { TextInput } from "../../../components/ui/inputs";
import { Button, RadioGroup } from "../../../components/ui";
import { BLOB_IMAGE_URL } from "../../../config/appConfig";
import { BlobContainerConsts } from "../../../models/constants/blobContainerConsts";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { copyToClipboard } from "../../../utils/clipboard";
import {
  DepositTypeListResponseList,
  BankTransferMethod,
  AvailableDepositBonusesList,
  DepositCreateResponse,
} from "../models/response";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { BottomDrawer } from "../../../components/ui/BottomDrawer";
import { GeneralPaymentHelp } from "./GeneralPaymentHelp";
import {
  DepositMethodType,
  TransferType,
  TransferTypeMaxLength,
  TransferTypeRegex,
  DepositType,
  DepositFlow,
} from "../models/constants";
import depositv1Service from "../services/deposit.service";
import { useNavigate } from "react-router-dom";
import { ConfirmationModal } from "../../../components/ui/modals/ConfirmationModal";
import { revertUserDepositById } from "../../transaction/services/transactions.service";
import localization from "../../../config/localization";
import userDeviceStore from "../../../store/device.store";
import { DeviceType } from "../../../models/constants/deviceType";
import { userPendingStore } from "../../users/store/userPendingData.store";
import { AccessMode } from "../../../models/constants/accessMode";

interface SubmitDepositRequestProps {
  selectedDepositType: DepositTypeListResponseList;
  transferTypeList: BankTransferMethod[];
  amount: string;
  onBack: () => void;
  selectedBonus: AvailableDepositBonusesList | null;
  depositFlow: string;
  depositCreateResponse: DepositCreateResponse | null;
  isQrDeposit: boolean;
  isBankTransfer: boolean;
}

export default function SubmitDepositRequest({
  selectedDepositType,
  transferTypeList,
  amount,
  onBack,
  selectedBonus,
  depositFlow,
  depositCreateResponse,
  isQrDeposit,
  isBankTransfer,
}: SubmitDepositRequestProps) {
  const deviceType = userDeviceStore((state) => state.deviceType);
  const isWebDevice = deviceType === DeviceType.Web;
  const hasUpiId = !!selectedDepositType?.allowedABCBankTransfers?.upiId;
  const isQrFlow =
    selectedDepositType.depositMethodType === DepositMethodType.QR ||
    isQrDeposit;
  const [selectedTransferType, setSelectedTransferType] = useState<string>(
    transferTypeList?.[0]?.Type ?? TransferType.IMPS.toString(),
  );
  const [selectedTransferImage, setSelectedTransferImage] = useState<
    string | null
  >(transferTypeList?.[0]?.Url ?? null);
  const [openTransferTypeImage, setOpenTransferTypeImage] = useState(false);
  const [openGeneralHelp, setOpenGeneralHelp] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState<string | null>(null);
  const [isValidReference, setIsValidReference] = useState<boolean | null>(
    null,
  );
  const [referenceError, setReferenceError] = useState<string | null>(null);
  const [isDepositRevertAllowed, setIsDepositRevertAllowed] =
    useState<boolean>(false);
  const [showDepositRevertModal, setShowDepositRevertModal] =
    useState<boolean>(false);
  const [submitClick, setSubmitClick] = useState<boolean>(false);
  const navigate = useNavigate();
  const accessModeSource = userDeviceStore((state) => state.accessModeSource);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    const depositConfig = await depositv1Service.getDepositConfiguration();
    const isDepositRevertEnabled = depositConfig?.details?.find(
      (x) => x.name === "Deposit_Revert",
    )?.value;
    setIsDepositRevertAllowed(isDepositRevertEnabled === "true");
  };

  const handleCopy = async () => {
    let accessMode = userDeviceStore.getState().accessModeSource.sourceType ?? AccessMode.Web;
    await copyToClipboard(
      accessMode,
      selectedDepositType.allowedABCBankTransfers
        ? selectedDepositType.allowedABCBankTransfers.accountNumber
        : selectedDepositType.accountNumber,
      {
        onSuccess: () => {
          toastService.success(localization.walletid_success_copied);

          setTimeout(() => { }, 5000);
        },
        onError: () => {
          toastService.error(localization.walletid_copy_failed);
        },
      },
    );
  };

  const handleOChangeClick = () => {
    if (
      selectedDepositType.depositMethodType &&
      selectedDepositType.depositMethodType === DepositMethodType.ABC &&
      selectedDepositType.name &&
      selectedDepositType.name.toLowerCase() !== DepositType.ABCCrypto
    ) {
      setShowDepositRevertModal(true);
      return;
    } else {
      onBack();
    }
    onBack();
  };

  const handleTransferTypeChange = (type: string) => {
    const selected = transferTypeList.find((x) => x.Type === type);

    setSelectedTransferType(type);
    setSelectedTransferImage(selected?.Url ?? null);
  };

  const handleOpenTransferTypeImage = () => {
    setOpenTransferTypeImage(true);
  };

  const handleHelpClick = () => {
    setOpenGeneralHelp(true);
  };

  const getMaxLengthByTransferType = () => {
    if (!selectedTransferType) return 0;

    return (
      TransferTypeMaxLength[
      selectedTransferType as keyof typeof TransferTypeMaxLength
      ] ?? 0
    );
  };

  const handleReferenceInput = (value: string) => {
    setReferenceNumber(value);

    if (!selectedTransferType) return;

    const regex = TransferTypeRegex[selectedTransferType];
    if (!regex) {
      setIsValidReference(null);
      return;
    }

    const isValid = regex.test(value);

    setIsValidReference(isValid);
    setReferenceError(
      isValid
        ? null
        : (
          localization.invalid_reference_number_for_type).replace("{type}", selectedTransferType),
    );
  };

  const handleDepositSubmit = async () => {
    if (submitClick) return;

    try {
      setSubmitClick(true);

      if (!referenceNumber) {
        toast.error(localization.enter_reference_number);
        return;
      }

      if (!isValidReference) {
        toast.error(localization.enter_valid_reference_number);
        return;
      }

      if (selectedDepositType.depositMethodType &&
        selectedDepositType.depositMethodType === DepositMethodType.ABC) {

        var result = await depositv1Service.handleAbcDepositSubmit(
          referenceNumber,
          selectedDepositType,
          depositCreateResponse,
          isBankTransfer
        );

        if (result && depositCreateResponse?.transactionid) {
          userPendingStore.getState().fetchPendingData();
          navigate("/deposit-under-approval", {
            state: {
              transactionId: depositCreateResponse.transactionid,
              amount,
              selectedDepositTypeName: selectedDepositType?.allowedABCBankTransfers?.bankName ?? selectedDepositType?.accountName,
              selectedDepositTypeImage: selectedDepositType?.allowedABCBankTransfers?.logoUrl
                ? selectedDepositType?.allowedABCBankTransfers?.logoUrl
                : `${BLOB_IMAGE_URL}${BlobContainerConsts.Banks}/${selectedDepositType?.logoUrl}`,
              isDepositRevertAllowed,
              depositRequestedId: depositCreateResponse?.id,
            },
          });
        }
      }
      else {
        var response = await depositv1Service.handleDepositSubmit(
          depositFlow,
          amount,
          referenceNumber,
          selectedTransferType,
          selectedDepositType,
          selectedBonus,
          accessModeSource.sourceType,
        );

        if (response && response.transactionid) {
          userPendingStore.getState().fetchPendingData();
          navigate("/deposit-under-approval", {
            state: {
              transactionId: response.transactionid,
              amount,
              selectedDepositTypeName: selectedDepositType?.name,
              selectedDepositTypeImage: `${BLOB_IMAGE_URL}${BlobContainerConsts.Banks}/${selectedDepositType?.logoUrl}`,
              isDepositRevertAllowed,
              depositRequestedId: response.id,
            },
          });
        }
      }
    }
    finally {
      setSubmitClick(false);
    }
  };

  const handleRevertDeposit = async () => {
    if (depositCreateResponse?.id) {
      var response = await revertUserDepositById(depositCreateResponse.id);
      if (response) {
        toast.success(localization.deposit_reverted_successfully);
        setShowDepositRevertModal(false);
        onBack();
      } else {
        setShowDepositRevertModal(false);
        navigate("/deposithistory");
      }
    }
  };

  const referenceInput = (
    <TextInput
      label={localization.ref_tran_utr}
      value={referenceNumber ?? ""}
      min={0}
      maxLength={getMaxLengthByTransferType()}
      onChange={(e) => setReferenceNumber(e.target.value)}
      error={referenceError ?? undefined}
      onInput={(e) => handleReferenceInput(e.currentTarget.value)}
      iconSrc={
        isValidReference === null
          ? undefined
          : isValidReference
            ? "/icons/ic-correct.svg"
            : "/icons/ic-wrong.svg"
      }
      helperText={
        depositFlow === DepositFlow.NewFlow &&
          selectedDepositType?.depositMethodType !== DepositMethodType.QR
          ? localization.needhelp
          : null
      }
      helperCallback={handleOpenTransferTypeImage}
      helperTextClassName={
        depositFlow === DepositFlow.NewFlow &&
          selectedDepositType?.depositMethodType !== DepositMethodType.QR
          ? "text-sm text-white underline hover:text-[#FF94B1]"
          : null
      }
    />
  );

  return (
    <div className="p-4">
      <div className="w-full flex justify-end items-center mb-3">
        <div className="flex items-center bg-[#1A1A1A] border border-[#2E2E2E] rounded-[6px] px-2 py-1 gap-2">
          <img
            src={
              selectedDepositType?.allowedABCBankTransfers?.logoUrl
                ? selectedDepositType?.allowedABCBankTransfers?.logoUrl
                : `${BLOB_IMAGE_URL}${BlobContainerConsts.Banks}/${selectedDepositType?.logoUrl}`
            }
            alt={selectedDepositType?.name}
            className="w-6 h-6 object-contain"
          />
          <p className="text-sm font-bold text-white">
            {selectedDepositType?.allowedABCBankTransfers?.bankName ?? selectedDepositType?.accountName}
          </p>
          <Button
            type="button"
            onClick={handleOChangeClick}
            className="text-xs font-medium bg-[#460413] text-[#FFBDCC] px-2 py-2 rounded-full hover:bg-[#8a0024] transition"
          >
            {localization.change}
          </Button>
        </div>
      </div>

      {selectedDepositType.depositMethodType === DepositMethodType.QR || isQrDeposit ? (
        <>
          {hasUpiId && (
            <div className="mt-10">
              <TextInput
                label={localization.upiid}
                value={selectedDepositType.allowedABCBankTransfers!.upiId}
                disabled
                faIcon={faCopy}
                onButtonClick={handleCopy}
              />
            </div>
          )}

          <div className="mt-10 flex justify-center items-center">
            <img
              src={
                isQrDeposit
                  ? depositCreateResponse?.upiqr
                  : selectedDepositType?.qrLogoUrl
                    ? `${BLOB_IMAGE_URL}${BlobContainerConsts.Banks}/${selectedDepositType.qrLogoUrl}`
                    : undefined
              }
              alt="QR Code"
              className="w-64 h-64 object-contain"
            />
          </div>

          <div className="mt-4 text-center">
            <p>{localization.scan_qr_to_pay}</p>
          </div>
        </>
      ) : (
        <div
          className={isWebDevice ? "mt-10 grid grid-cols-2 gap-4" : ""}
        >
          <div className={isWebDevice ? "mt-0" : "mt-10"}>
            <TextInput
              label={localization.wallet_id}
              value={
                selectedDepositType.allowedABCBankTransfers
                  ? selectedDepositType.allowedABCBankTransfers.accountNumber
                  : (selectedDepositType.accountNumber ?? "-")
              }
              disabled={true}
              faIcon={faCopy}
              onButtonClick={handleCopy}
            />
          </div>

          <div className={isWebDevice ? "mt-0" : "mt-4"}>
            <TextInput
              label={
                selectedDepositType.allowedABCBankTransfers
                  ? localization.bank_name_label
                  : localization.accountname
              }
              value={
                selectedDepositType.allowedABCBankTransfers?.bankName ??
                selectedDepositType.accountName ??
                "-"
              }
              disabled={true}
            />
          </div>

          {(selectedDepositType.ifsc ||
            selectedDepositType.allowedABCBankTransfers?.ifsc) && (
              <div className={isWebDevice ? "mt-0" : "mt-4"}>
                <TextInput
                  label={localization.ifsc}
                  value={
                    selectedDepositType.allowedABCBankTransfers
                      ? selectedDepositType.allowedABCBankTransfers.ifsc
                      : selectedDepositType.ifsc
                  }
                  disabled={true}
                />
              </div>
            )}

          <div className={isWebDevice ? "mt-0" : "mt-4"}>
            {referenceInput}
          </div>
        </div>
      )}

      {(selectedDepositType.depositMethodType === DepositMethodType.QR ||
        isQrDeposit) && <div className="mt-4">{referenceInput}</div>}

      {transferTypeList.length > 0 && (
        <div className="mt-4">
          <RadioGroup
            label={localization.transferred_by}
            name="transferType"
            value={selectedTransferType}
            onChange={handleTransferTypeChange}
            options={transferTypeList.map((item) => ({
              label: item.Type,
              value: item.Type,
            }))}
          />
        </div>
      )}

      {selectedDepositType.depositMethodType !==
        DepositMethodType.QR && depositFlow === DepositFlow.OldFlow && (
          <div className="mt-6">
            <div className="flex items-center justify-between gap-4 pl-1">
              <div className="flex items-center gap-3">
                <div
                  className="relative w-14 h-14 rounded-[10px] bg-[#1A1A1A] overflow-hidden cursor-pointer"
                  onClick={handleOpenTransferTypeImage}
                >
                  <img
                    src={
                      selectedTransferImage
                        ? `${BLOB_IMAGE_URL}${BlobContainerConsts.Banks}/${selectedTransferImage}`
                        : ""
                    }
                    alt="UTR Help"
                    className="w-full h-full object-cover opacity-50"
                  />

                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex justify-center">
                      <FontAwesomeIcon
                        icon={faMagnifyingGlass}
                        className="text-white text-sm w-6 h-6"
                      />
                    </div>
                  </div>
                </div>

                <p className="text-sm font-bold text-[#A9A9A9] leading-snug">
                  {localization.refid_note}
                  <br />
                  {localization.ref_tran_utr}
                </p>
              </div>

              <Button
                type="submit"
                className="px-4 py-3 text-xs font-bold text-white bg-[#1A1A1A] border border-[#2E2E2E] rounded-[10px] hover:bg-[#2A2A2A] transition"
                onClick={() => handleHelpClick()}
              >
                {localization.needhelp}
              </Button>
            </div>
          </div>
        )}

      <Button
        type="submit"
        className={`mt-8 ${isWebDevice ? "w-[40%] block mx-auto" : "w-full"} rounded py-4 text-white bg-primary-gradient ${submitClick ? "opacity-50 cursor-not-allowed" : ""}`}
        onClick={handleDepositSubmit}
        disabled={submitClick}
      >
        <span>{submitClick ? localization.processing : localization.deposit_btn}</span>
      </Button>

      <BottomDrawer
        open={openTransferTypeImage}
        onClose={() => {
          setOpenTransferTypeImage(false);
          setOpenGeneralHelp(false);
        }}
        className="w-full lg:max-w-[80%] lg:mx-auto"
      >
        <div className="relative w-full max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <p className="text-white text-xl font-semibold">{localization.find_your_utr}</p>

          <div className="max-h-[80vh] overflow-y-auto scrollbar-none scrollbar-hide">
            <div className="mt-6 rounded-xl bg-black/20 flex items-center justify-center overflow-hidden
                    w-full aspect-[4/3] sm:aspect-[16/9]">
              {selectedTransferImage && (
                <img
                  src={`${BLOB_IMAGE_URL}${BlobContainerConsts.Banks}/${selectedTransferImage}`}
                  alt="UTR Help"
                  className="max-h-[70vh] w-full h-full object-contain"
                />
              )}
            </div>
            <div className="mt-6">
              <GeneralPaymentHelp
                open={openGeneralHelp}
                onToggle={setOpenGeneralHelp}
                amount={amount}
              />
            </div>
          </div>
        </div>
      </BottomDrawer>
      {openGeneralHelp && (
        <GeneralPaymentHelp
          open={openGeneralHelp}
          onToggle={setOpenGeneralHelp}
          amount={amount}
        />
      )}

      {showDepositRevertModal && (
        <ConfirmationModal
          isOpen={showDepositRevertModal}
          onConfirm={() => {
            handleRevertDeposit();
          }}
          onCancel={() => {
            setShowDepositRevertModal(false);
          }}
          title={localization.confirm_revert_deposit}
          description={`\u20B9${Number(amount)?.toFixed(2)}`}
          descriptionClassName="text-center text-[34px] font-medium"
          icon={"/icons/ic-info.svg"}
          blurColor="bg-[#FFE6CC]"
          confirmText={localization.yes}
          cancelText={localization.no}
        />
      )}

    </div>
  );
}
